import type { ReactNode } from "react";
import { Tooltip } from "../Feedback/Tooltip";
import { headingFontStyle } from "../Theme/headingFontStyle";

interface StepHeaderProps {
  /** The step indicator shown in the circle (e.g. a number). */
  step: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Info bubble shown next to the subtitle. */
  tooltip?: string | undefined;
}

/**
 * The centered chrome at the top of a multi-step flow: a brand-accent circle
 * with the step indicator, a large title, and an optional subtitle (with an
 * info bubble). Used by the quote-flow steps (Shipment Contents, Insurance…).
 */
export function StepHeader({
  step,
  title,
  subtitle,
  tooltip,
}: StepHeaderProps) {
  return (
    <div className="flex flex-col items-center text-center">
      {/* 48px and an 18px gap to the title, matching the step badge on the
          dashboard's quoting flow that this reproduces. */}
      <div
        className={`mb-[18px] flex h-12 w-12 items-center justify-center rounded-full bg-brand text-base font-bold text-white`}
      >
        {step}
      </div>
      {/* A flex row, not plain inline text: something set beside the title
          (an "Optional" badge) centres on it instead of sitting on the 28px
          text's baseline, where it hangs low. Centring on the line still reads
          high, because the heading font leaves more room above its letters
          than below. The nudge moves what's beside the title down 3px, halfway
          between the middle of the capitals (1.5px) and the middle of the
          lowercase letters (4.4px) in this font at 28px; chosen by eye against
          both. */}
      <span
        className={`inline-flex flex-wrap items-center justify-center gap-x-2 text-[28px] font-semibold text-foreground [&>*]:translate-y-[3px]`}
        style={headingFontStyle()}
      >
        {title}
      </span>
      {subtitle && (
        // No top margin — the title's line box already separates them, and a
        // gap here detaches the subtitle from the title it belongs to.
        //
        // Capped so the subtitle stays a readable column instead of running the
        // full width of a wide page — a step's explanation is a paragraph, and
        // at ~1200px it reads as a single unbroken line.
        <div className="flex max-w-[618px] items-center gap-1.5">
          <span className={`text-sm font-light text-foreground`}>
            {subtitle}
          </span>
          {tooltip && <Tooltip variant="info" content={tooltip} />}
        </div>
      )}
    </div>
  );
}
